import * as React from 'react';
import * as _ from 'lodash';
import { Dispatch, connect } from 'react-redux';
import { IBillingState, IBillingDetailResponse, IApplicationState, AppActionCreators, BillingActionCreators, IAppState } from '@nkr/store/index';
import { IRecipientCenterInvoiceRequest } from '@nkr/models/index';
import { ChargeStatus, ChargeTypes, BillableNephrectomyStatus } from '@nkr/enums/index';
import { Button, Panel, Alert } from 'react-bootstrap';
import ViewChargesTableRow from './view-charges-table-row';
import DisputeCommentsTableRow from './dispute-comments-table-row';
import SwapInfo from '../swap-info';
import { TableWrapper } from './styles';
import { NavSubWrapper } from '../../styles';
import NavDonorCenter from '../nav-donor-center';

interface IOwnProps {
    donorId: number
    centerId: number,
    centerAlias: string,
    tabText: string,
    onLoaded: (tabText: string) => void
}

interface IStateProps {
    billing: IBillingState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDetails: (donorId: number, centerId: number) => Promise<IBillingDetailResponse>,
    saveRecipientInvoiceDetails: (request: IRecipientCenterInvoiceRequest) => Promise<IBillingDetailResponse>,
    getFile: (donorId: number, docType: string, fileName: string) => void
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

interface IState {
    physicianCharges: {
        status: number,
        comments: {
            edit: boolean,
            value: string,
            valid: boolean
        }
    },
    anesthesiaCharges: {
        status: number,
        comments: {
            edit: boolean,
            value: string,
            valid: boolean
        }
    },
    hospitalCharges: {
        status: number,
        comments: {
            edit: boolean,
            value: string,
            valid: boolean
        }
    },
    alert: {
        show: boolean,
        success: boolean,
        message: string | React.ReactNode
    }
}

class ViewCharges extends React.Component<TProps, IState> {
    readonly state: IState = {
        physicianCharges: {
            status: 2,
            comments: {
                edit: false,
                value: '',
                valid: true
            }
        },
        anesthesiaCharges: {
            status: -1,
            comments: {
                edit: false,
                value: '',
                valid: true
            }
        },
        hospitalCharges: {
            status: -1,
            comments: {
                edit: false,
                value: '',
                valid: true
            }
        },
        alert: {
            show: false,
            success: true,
            message: ''
        }
    }

    componentWillMount = () => {
        this.getData();
    }

    getData = () => {
        let { donorId, centerId } = this.props;

        this.props.showLoading('Getting billing details, one moment please...').then(() => {
            this.props.getDetails(donorId, centerId).then((response: IBillingDetailResponse) => {
                this.props.hideLoading();

                if (response.data.success) {
                    let { isSbs, isMedicare, isMedicaid, isGlobal, billableNephrectomy } = response.data.results;
                    let { physicianCharges, physicianChargeStatusId, physicianChargeComments, anesthesiaChargeStatusId, anesthesiaChargeComments, hospitalChargeStatusId, hospitalChargeComments } = billableNephrectomy;
                    let isNonGlobal = !isSbs && !isMedicare && !isMedicaid && !isGlobal;

                    this.setState({
                        physicianCharges: {
                            status: isSbs || isMedicare || isMedicaid || isNonGlobal || physicianCharges == 0 ? ChargeStatus.Accepted : physicianChargeStatusId,
                            comments: {
                                edit: false,
                                value: physicianChargeComments,
                                valid: true
                            }
                        },
                        anesthesiaCharges: {
                            status: isSbs || isMedicare || isMedicaid || isNonGlobal ? ChargeStatus.Accepted : anesthesiaChargeStatusId,
                            comments: {
                                edit: false,
                                value: anesthesiaChargeComments,
                                valid: true
                            }
                        },
                        hospitalCharges: {
                            status: isSbs ? 1 : hospitalChargeStatusId,
                            comments: {
                                edit: false,
                                value: hospitalChargeComments,
                                valid: true
                            }
                        }
                    }, () => {
                        let { chainId, clusterNumber, positionNumber } = this.props.billing.details.billableNephrectomy;
                        let tabText = `Review Invoice for Swap ${chainId}/${clusterNumber}/${positionNumber}`;

                        this.props.onLoaded(tabText);
                    });
                }
            });
        });
    }

    handleStatusChange = (status: number, chargeType: ChargeTypes) => {
        let physicianCharges = _.cloneDeep(this.state.physicianCharges);
        let anesthesiaCharges = _.cloneDeep(this.state.anesthesiaCharges);
        let hospitalCharges = _.cloneDeep(this.state.hospitalCharges);

        if (chargeType == ChargeTypes.PHYSICIAN_CHARGES) {
            physicianCharges.status = status;
            physicianCharges.comments.value = status == 1 ? '' : physicianCharges.comments.value;
        }

        if (chargeType == ChargeTypes.ANESTHESIA_CHARGES) {
            anesthesiaCharges.status = status;
            anesthesiaCharges.comments.value = status == 1 ? '' : anesthesiaCharges.comments.value;
        }

        if (chargeType == ChargeTypes.HOSPITAL_CHARGES) {
            hospitalCharges.status = status;
            hospitalCharges.comments.value = status == 1 ? '' : hospitalCharges.comments.value;
        }

        this.setState({
            physicianCharges,
            anesthesiaCharges,
            hospitalCharges
        });
    }

    handleEditComments = (chargeType: ChargeTypes) => {
        let physicianCharges = _.cloneDeep(this.state.physicianCharges);
        let anesthesiaCharges = _.cloneDeep(this.state.anesthesiaCharges);
        let hospitalCharges = _.cloneDeep(this.state.hospitalCharges);

        if (chargeType == ChargeTypes.PHYSICIAN_CHARGES) {
            physicianCharges.comments.edit = true;
        }

        if (chargeType == ChargeTypes.ANESTHESIA_CHARGES) {
            anesthesiaCharges.comments.edit = true;
        }

        if (chargeType == ChargeTypes.HOSPITAL_CHARGES) {
            hospitalCharges.comments.edit = true;
        }

        this.setState({
            physicianCharges,
            anesthesiaCharges,
            hospitalCharges
        });
    }

    handleSubmitComments = (comments: string, chargeType: ChargeTypes) => {
        let physicianCharges = _.cloneDeep(this.state.physicianCharges);
        let anesthesiaCharges = _.cloneDeep(this.state.anesthesiaCharges);
        let hospitalCharges = _.cloneDeep(this.state.hospitalCharges);

        if (chargeType == ChargeTypes.PHYSICIAN_CHARGES) {
            physicianCharges.comments.value = comments;
            physicianCharges.comments.edit = false;
            physicianCharges.comments.valid = comments != '';
        }

        if (chargeType == ChargeTypes.ANESTHESIA_CHARGES) {
            anesthesiaCharges.comments.value = comments;
            anesthesiaCharges.comments.edit = false;
            anesthesiaCharges.comments.valid = comments != '';
        }

        if (chargeType == ChargeTypes.HOSPITAL_CHARGES) {
            hospitalCharges.comments.value = comments;
            hospitalCharges.comments.edit = false;
            hospitalCharges.comments.valid = comments != '';
        }

        this.setState({
            physicianCharges,
            anesthesiaCharges,
            hospitalCharges
        });
    }

    handleCancelComments = (chargeType: ChargeTypes) => {
        let physicianCharges = _.cloneDeep(this.state.physicianCharges);
        let anesthesiaCharges = _.cloneDeep(this.state.anesthesiaCharges);
        let hospitalCharges = _.cloneDeep(this.state.hospitalCharges);

        if (chargeType == ChargeTypes.PHYSICIAN_CHARGES) {
            physicianCharges.comments.edit = false;
        }

        if (chargeType == ChargeTypes.ANESTHESIA_CHARGES) {
            anesthesiaCharges.comments.edit = false;
        }

        if (chargeType == ChargeTypes.HOSPITAL_CHARGES) {
            hospitalCharges.comments.edit = false;
        }

        this.setState({
            physicianCharges,
            anesthesiaCharges,
            hospitalCharges,
        });
    }

    handleGetDocumentation = (donorId: number, docType: string, fileName: string) => {
        this.props.getFile(donorId, docType, fileName);
    }

    handleSave = () => {
        let { physicianCharges, anesthesiaCharges, hospitalCharges } = _.cloneDeep(this.state);
        let { donor, recipient } = this.props.billing.details.billableNephrectomy;
        let { physicianChargesValid, anesthesiaChargesValid, hospitalChargesValid, message } = this.validate();

        if (message == null) {
            let request: IRecipientCenterInvoiceRequest = {
                donorId: donor.id,
                recipientId: recipient.id,
                physicianChargeStatus: physicianCharges.status,
                physicianChargeComments: physicianCharges.comments.value,
                anesthesiaChargeStatus: anesthesiaCharges.status,
                anesthesiaChargeComments: anesthesiaCharges.comments.value,
                hospitalChargeStatus: hospitalCharges.status,
                hospitalChargeComments: hospitalCharges.comments.value
            };

            this.props.showLoading('Saving the items, one moment please...').then(() => {
                this.props.saveRecipientInvoiceDetails(request).then((response) => {
                    this.props.hideLoading();

                    let accepted = physicianCharges.status == 1 && anesthesiaCharges.status == 1 && hospitalCharges.status == 1;
                    let message = accepted ? 'All charges will appear on your NKR invoice.' : 'This invoice has been returned to the donor center.';

                    this.setState({
                        alert: {
                            show: true,
                            success: response.data.success,
                            message: response.data.success ? message : 'We encountered an error while attempting to save the line items.'
                        }
                    });
                });
            });
        } else {
            physicianCharges.comments.valid = physicianChargesValid;
            anesthesiaCharges.comments.valid = anesthesiaChargesValid;
            hospitalCharges.comments.valid = hospitalChargesValid;

            this.setState({
                physicianCharges,
                anesthesiaCharges,
                hospitalCharges,
                alert: {
                    show: true,
                    success: false,
                    message
                }
            });
        }
    }

    handleCancel = () => {
        window.location.href = '/center/' + this.props.centerId + '/billing/billed-to-center';
    }

    validate = (): { physicianChargesValid: boolean, anesthesiaChargesValid: boolean, hospitalChargesValid: boolean, message: React.ReactNode } => {
        let { physicianCharges, anesthesiaCharges, hospitalCharges } = _.cloneDeep(this.state);

        let messages: string[] = [];

        let results = {
            physicianChargesValid: true,
            anesthesiaChargesValid: true,
            hospitalChargesValid: true,
            message: null
        };

        if (physicianCharges.status == ChargeStatus.Unknown) {
            results.physicianChargesValid = false;
            messages.push('Please accept or decline the Physician Donor Nephrectomy charge.')
        }

        if (physicianCharges.status == ChargeStatus.Disputed && physicianCharges.comments.value == '') {
            results.physicianChargesValid = false;
            messages.push('Please provide a reason for disputing the Physician Donor Nephrectomy charge.')
        }

        if (anesthesiaCharges.status == ChargeStatus.Unknown) {
            results.anesthesiaChargesValid = false;
            messages.push('Please accept or decline the Anesthesia charge.')
        }

        if (anesthesiaCharges.status == ChargeStatus.Disputed && anesthesiaCharges.comments.value == '') {
            results.anesthesiaChargesValid = false;
            messages.push('Please provide a reason for disputing the Anesthesia charge.')
        }

        if (hospitalCharges.status == ChargeStatus.Unknown) {
            results.hospitalChargesValid = false;
            messages.push('Please accept or decline the Hospital Donor Nephrectomy charge.')
        }

        if (hospitalCharges.status == ChargeStatus.Disputed && hospitalCharges.comments.value == '') {
            results.hospitalChargesValid = false;
            messages.push('Please provide a reason for disputing the Hospital Donor Nephrectomy charge.')
        }

        if (messages.length > 0) {
            results.message =
                <div>
                    <div>The following issues prevented the submission of the invoice:</div>
                    <div style={{ marginTop: 5 }}>
                        {messages.map((message, index) =>
                            <div key={index}>&middot; {message}</div>
                        )}
                    </div>
                </div>;
        }

        return results;
    }

    getChargeAmount = (charges: number, chargeType: ChargeTypes) => {
        let { isSbs, isMedicare, isMedicaid, isGlobal, asaUnitPrice, asaUnits } = this.props.billing.details;

        if (isSbs) {
            return charges.toLocaleString('en', { minimumFractionDigits: 2 });
        }

        if (isMedicare) {
            return 'Billed through Medicare using recipient\'s Medicare #';
        }

        if (isMedicaid) {
            if (chargeType == ChargeTypes.PHYSICIAN_CHARGES)
                return 'NKR will remit $2,142';
            else
                return 'NKR will remit $1,071';
        }

        if (isGlobal) {
            if (chargeType == ChargeTypes.PHYSICIAN_CHARGES) {
                // let physicianCharge = this.props.billing.details.billableNephrectomy.donor.center.physicianDonorNephrectomyCharges * 1.5;

                return charges.toLocaleString('en', { minimumFractionDigits: 2 });
            } else {
                let amount = (asaUnitPrice * asaUnits).toLocaleString('en', { minimumFractionDigits: 2 });

                return <>{amount}<br /><span style={{ fontSize: 12 }}>({asaUnits} units @ ${asaUnitPrice})</span></>;
            }
        }

        if (!isSbs && !isMedicare && !isMedicaid && !isGlobal) {
            return 'Bill private insurance directly';
        }
    }

    getHospitalChargeAmount = (charges: number) => {
        return charges ? charges.toLocaleString('en', { minimumFractionDigits: 2 }) : '';
    }

    disableSaveButton = () => {
        let { physicianCharges, anesthesiaCharges, hospitalCharges } = this.state;
        let physician = physicianCharges.status == ChargeStatus.Unknown;
        let anesthesia = anesthesiaCharges.status == ChargeStatus.Unknown;
        let hospital = hospitalCharges.status == ChargeStatus.Unknown;

        return physician || anesthesia || hospital;
    }

    render = () => {
        const details = this.props.billing.details;

        if (details == null)
            return null;

        const { chainId, clusterNumber, positionNumber, transplantDate, donor, recipient, physicianCharges, anesthesiaCharges, hospitalCharges } = details.billableNephrectomy;
        const physicianDonorNephrectomyCharges = donor.rdnCenter == null ? donor.center.physicianDonorNephrectomyCharges : donor.rdnCenter.physicianDonorNephrectomyCharges;
        const updatedPhysicianCharges = details.isGlobal ? physicianDonorNephrectomyCharges * 1.5 : physicianCharges;
        const physicianChargeAmount = this.getChargeAmount(updatedPhysicianCharges, ChargeTypes.PHYSICIAN_CHARGES);
        const anesthesiaChargeAmount = this.getChargeAmount(anesthesiaCharges, ChargeTypes.ANESTHESIA_CHARGES);
        const hospitalChargeAmount = this.getHospitalChargeAmount(hospitalCharges);
        const total = updatedPhysicianCharges + (details.isGlobal ? details.asaUnits * details.asaUnitPrice : anesthesiaCharges) + hospitalCharges;
        const saveButtonLabel = 'Submit';
        const disableSaveButton = this.disableSaveButton();
        const { isSbs, isMedicare, isMedicaid, isGlobal } = details;
        const isNonGlobal = !isSbs && !isMedicare && !isMedicaid && !isGlobal;
        const widths = [22.0, 7.3, 33.33, 20.6, 16.6];
        const isAdmin = this.props.app.user.group.isSuper;
        const sentToRecipientCenter = this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter;

        return (
            <>
                <div style={{ marginBottom: 10, borderWidth: '0px 1px 1px 1px', borderStyle: 'solid', borderColor: '#ddd', borderBottomLeftRadius: 4, borderBottomRightRadius: 4, padding: '10px 5px 0px 5px' }}>
                    <NavSubWrapper verticalAlign='center' minTabWidth='85' fontSize='12px'>
                        <NavDonorCenter
                            section={'VIEW-CHARGES'}
                            tabText={this.props.tabText}
                            centerId={this.props.centerId}
                            centerAlias={this.props.centerAlias}
                        />
                    </NavSubWrapper>

                    <div className='container-fluid' style={{ width: '80%' }}>
                        {isAdmin && (
                            <div className="row" style={{ marginTop: 0 }}>
                                <div className="col-xs-12">
                                    <Alert bsStyle='warning'>
                                        You are currently logged in as an Admin; you can change any items applicable available based on the insurance.
                                    </Alert>
                                </div>
                            </div>
                        )}

                        {this.state.alert.show && (
                            <div style={{ marginTop: 10 }}>
                                <Alert bsStyle={this.state.alert.success ? 'success' : 'danger'}>{this.state.alert.message}</Alert>
                            </div>
                        )}

                        {this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter && (
                            <Alert bsStyle='warning'>
                                All line items must either be accepted or disputed to be able to submit...
                            </Alert>
                        )}

                        <SwapInfo
                            chainId={chainId}
                            clusterNumber={clusterNumber}
                            positionNumber={positionNumber}
                            transplantDate={transplantDate}
                            isSbs={details.isSbs}
                            isGlobal={details.isGlobal}
                            recipient={recipient}
                            donor={donor}
                        />

                        <Panel>
                            <Panel.Heading style={{ fontSize: 16 }}>
                                Invoice Line Items
                            </Panel.Heading>

                            <Panel.Body>
                                <TableWrapper>
                                    <thead>
                                        <tr>
                                            <th style={{ width: widths[0] + '%' }}>Line Item</th>
                                            <th style={{ width: widths[1] + '%' }}></th>
                                            <th style={{ width: widths[2] + '%' }}>Amount</th>
                                            <th style={{ width: widths[3] + '%' }}>Documentation</th>
                                            <th style={{ width: widths[4] + '%' }}>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <ViewChargesTableRow
                                            donorId={donor.id}
                                            chargeDescription='Physician Donor Nephrectomy'
                                            chargeAmount={physicianChargeAmount}
                                            chargeType={ChargeTypes.PHYSICIAN_CHARGES}
                                            claimType={{
                                                isSbs,
                                                isMedicare,
                                                isMedicaid,
                                                isGlobal
                                            }}
                                            file={details.physicianChargesFile}
                                            status={this.state.physicianCharges.status}
                                            allowAction={this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter && !isSbs && !isMedicare && !isMedicaid && !isNonGlobal && physicianCharges > 0}
                                            backgroundColor='white'
                                            isAdmin={isAdmin}
                                            onStatusChange={this.handleStatusChange}
                                            onGetDocumentation={this.handleGetDocumentation}
                                        />

                                        {this.state.physicianCharges.status == ChargeStatus.Disputed && (
                                            <DisputeCommentsTableRow
                                                edit={this.state.physicianCharges.comments.edit}
                                                valid={this.state.physicianCharges.comments.valid}
                                                comments={this.state.physicianCharges.comments.value}
                                                chargeType={ChargeTypes.PHYSICIAN_CHARGES}
                                                allowChange={this.props.billing.details.billableNephrectomy.id == 0 || isAdmin}
                                                backgroundColor='white'
                                                onEditComments={this.handleEditComments}
                                                onSubmitComments={this.handleSubmitComments}
                                                onCancelComments={this.handleCancelComments}
                                            />
                                        )}

                                        <ViewChargesTableRow
                                            donorId={donor.id}
                                            chargeDescription='Anesthesia'
                                            chargeAmount={anesthesiaChargeAmount}
                                            chargeType={ChargeTypes.ANESTHESIA_CHARGES}
                                            claimType={{
                                                isSbs,
                                                isMedicare,
                                                isMedicaid,
                                                isGlobal
                                            }}
                                            file={details.anesthesiaChargesFile}
                                            status={this.state.anesthesiaCharges.status}
                                            allowAction={this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter && !isSbs && !isMedicare && !isMedicaid && !isNonGlobal}
                                            backgroundColor='#eee'
                                            isAdmin={isAdmin}
                                            onStatusChange={this.handleStatusChange}
                                            onGetDocumentation={this.handleGetDocumentation}
                                        />

                                        {this.state.anesthesiaCharges.status == ChargeStatus.Disputed && (
                                            <DisputeCommentsTableRow
                                                edit={this.state.anesthesiaCharges.comments.edit}
                                                valid={this.state.anesthesiaCharges.comments.valid}
                                                comments={this.state.anesthesiaCharges.comments.value}
                                                chargeType={ChargeTypes.ANESTHESIA_CHARGES}
                                                allowChange={this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter || isAdmin}
                                                backgroundColor='#eee'
                                                onEditComments={this.handleEditComments}
                                                onSubmitComments={this.handleSubmitComments}
                                                onCancelComments={this.handleCancelComments}
                                            />
                                        )}

                                        <ViewChargesTableRow
                                            donorId={donor.id}
                                            chargeDescription='Hospital Donor Nephrectomy'
                                            chargeAmount={hospitalChargeAmount}
                                            chargeType={ChargeTypes.HOSPITAL_CHARGES}
                                            claimType={{
                                                isSbs,
                                                isMedicare,
                                                isMedicaid,
                                                isGlobal
                                            }}
                                            file={details.hospitalChargesFile}
                                            status={this.state.hospitalCharges.status}
                                            allowAction={this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter && !isSbs}
                                            backgroundColor='white'
                                            isAdmin={isAdmin}
                                            onStatusChange={this.handleStatusChange}
                                            onGetDocumentation={this.handleGetDocumentation}
                                        />

                                        {this.state.hospitalCharges.status == ChargeStatus.Disputed && (
                                            <DisputeCommentsTableRow
                                                edit={this.state.hospitalCharges.comments.edit}
                                                valid={this.state.hospitalCharges.comments.valid}
                                                comments={this.state.hospitalCharges.comments.value}
                                                chargeType={ChargeTypes.HOSPITAL_CHARGES}
                                                allowChange={this.props.billing.details.billableNephrectomy.statusId == BillableNephrectomyStatus.SentToRecipientCenter || isAdmin}
                                                backgroundColor='white'
                                                onEditComments={this.handleEditComments}
                                                onSubmitComments={this.handleSubmitComments}
                                                onCancelComments={this.handleCancelComments}
                                            />
                                        )}

                                        <tr>
                                            <td style={{ fontWeight: 500, fontSize: 16, paddingTop: 0, paddingBottom: 0, verticalAlign: 'top' }}>
                                                <div style={{ paddingTop: 5 }}>TOTAL CHARGES</div>
                                            </td>
                                            <td>&nbsp;</td>
                                            <td style={{ fontWeight: 500, fontSize: 18, paddingTop: 0, paddingBottom: 0, verticalAlign: 'top' }}>
                                                <div style={{ paddingTop: 5, paddingBottom: 5, marginLeft: 'auto', marginRight: 'auto', width: '50%', borderTop: '1px solid black', borderBottom: '3px double black' }}>${total.toLocaleString('en', { minimumFractionDigits: 2 })}</div>
                                            </td>
                                            <td colSpan={2}>&nbsp;</td>
                                        </tr>

                                    </tbody>
                                </TableWrapper>
                            </Panel.Body>
                        </Panel>

                        {(sentToRecipientCenter || isAdmin) && (
                            <div style={{ marginTop: 30, marginBottom: 15, display: 'flex', justifyContent: 'center' }}>
                                <Button bsStyle='primary' bsSize='large' disabled={disableSaveButton} onClick={this.handleSave}>{saveButtonLabel}</Button>
                                <Button bsStyle='link' onClick={this.handleCancel}>Cancel and Return to the List...</Button>
                            </div>
                        )}
                    </div>
                </div>
            </>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        billing: appState.billing,
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => { return dispatch(AppActionCreators.showLoadingAsync(message)); },
        hideLoading: () => { dispatch(AppActionCreators.hideLoading()); },
        getDetails: (donorId, centerId) => { return dispatch(BillingActionCreators.getDetails(donorId, centerId)); },
        saveRecipientInvoiceDetails: (request) => { return dispatch(BillingActionCreators.saveRecipientInvoiceDetails(request)); },
        getFile: (donorId, docType, fileName) => { dispatch(BillingActionCreators.getFile(donorId, docType, fileName)); }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(ViewCharges);